import { Box } from '@mui/material';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CurveType } from '@mui/x-charts';
import { WidgetOptions } from '../dashboardTypes.js';
import OrientationWidget from './OrientationWidget.js';
import FieldWidget from './FieldWidget.js';
import ConsoleWidget from './ConsoleWidget.js';
import TimerWidget from './TimerWidget.js';
import CountdownTimerWidget from './CountdownTimerWidget.js';
import { COUNTDOWN_TIMER_WIDGET_TYPE } from '../../../utils/countdownTimerCodeUtils.js';
import GraphWidget from './GraphWidget.js';
import PositionWidget from './PositionWidget.js';
import GaugeWidget from './GaugeWidget.js';
import BooleanWidget from './BooleanWidget.js';
import { useGridStackContext } from '../GridStack/grid-stack-context.js';
import WidgetSettingsHeader from './WidgetSettingsHeader.js';
import { WidgetHeaderProvider, type WidgetHeaderConfig } from './WidgetHeaderContext.js';
import { mergeWidgetHeaderConfig, widgetHeaderConfigsEqual } from './widgetHeaderConfig.js';
import { defaultWidgetTitle } from './widgetTitle.js';
import { motorPortsFromStreamKeys } from '../../../utils/electronicsDashboardUtils.js';
import { BOOLEAN_WIDGET_TYPE, isMotorStreamSensorKey } from '../../Graphs/sensor-utils.js';
import { isLineChartWidgetType } from '../../../utils/dashboardWidgetUtils.js';
import { GraphNoDataMessage } from '../../Graphs/GraphNoDataMessage.js';
import { useDashboardCapabilities } from '../dashboardCapabilities.js';
import { stableJsonSignature } from '../../../utils/stableJsonSignature.js';

interface WidgetBaseProps {
  type: string;
  curve: CurveType;
  id: string;
  style: string;
  dataKey: string;
  sensorKeys?: string[];
  options?: WidgetOptions;
}

const WidgetBase = ({ type, curve, id, style, dataKey, sensorKeys, options }: WidgetBaseProps) => {
  const { t } = useTranslation();
  const { removeWidget } = useGridStackContext();
  const { canDeleteWidgets, isReadOnlyProject } = useDashboardCapabilities();

  const optionsSignature = stableJsonSignature(options ?? null);

  const defaultTitle = useMemo(() => {
    const motorMechanismContext =
      type === BOOLEAN_WIDGET_TYPE && isMotorStreamSensorKey(dataKey) && !options?.selectedMotorPorts?.length && sensorKeys?.length
        ? { selectedMotorPorts: motorPortsFromStreamKeys(sensorKeys, dataKey) }
        : undefined;
    return defaultWidgetTitle(type, dataKey, t, options, motorMechanismContext);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- options read for title; optionsSignature avoids parse-time object identity churn
  }, [type, dataKey, t, optionsSignature, sensorKeys, options?.selectedMotorPorts]);

  const bodyIdentityKey = `${id}:${type}:${dataKey}`;
  const [headerConfig, setHeaderConfigState] = useState<WidgetHeaderConfig>(() => ({
    title: defaultTitle,
    menuItems: undefined,
    toolbarEnd: undefined,
  }));
  const [trackedBodyIdentityKey, setTrackedBodyIdentityKey] = useState(bodyIdentityKey);

  if (bodyIdentityKey !== trackedBodyIdentityKey) {
    setTrackedBodyIdentityKey(bodyIdentityKey);
    setHeaderConfigState({
      title: defaultTitle,
      menuItems: undefined,
      toolbarEnd: undefined,
    });
  }

  const setHeaderConfig = useCallback((config: WidgetHeaderConfig) => {
    setHeaderConfigState((prev) => {
      const next = mergeWidgetHeaderConfig(prev, config);
      if (widgetHeaderConfigsEqual(prev, next)) {
        return prev;
      }
      return next;
    });
  }, []);

  const isPositionFieldWidget = type === 'pos' && dataKey === 'all';
  const isSpeedWidget = type === 'speed';
  const isBooleanWidget = type === BOOLEAN_WIDGET_TYPE;
  const isCountdownTimerWidget = type === COUNTDOWN_TIMER_WIDGET_TYPE;
  const showGraphWidget = isLineChartWidgetType(type, dataKey);

  const hasKnownBody =
    showGraphWidget ||
    isPositionFieldWidget ||
    isSpeedWidget ||
    isBooleanWidget ||
    type === 'orientation' ||
    type === 'field' ||
    type === 'console' ||
    type === 'timer' ||
    isCountdownTimerWidget;

  const handleWidgetDelete = useCallback(() => {
    removeWidget(id);
  }, [id, removeWidget]);

  let body: ReactNode = null;

  if (showGraphWidget) {
    body = <GraphWidget id={id} curve={curve} style={style} type={type} dataKey={dataKey} sensorKeys={sensorKeys} options={options} />;
  } else if (isPositionFieldWidget) {
    body = <PositionWidget id={id} style={style} />;
  } else if (isSpeedWidget) {
    body = <GaugeWidget id={id} style={style} />;
  } else if (type === 'orientation') {
    body = <OrientationWidget id={id} style={style} />;
  } else if (type === 'field') {
    body = <FieldWidget id={id} widgetFieldType={options?.fieldType} />;
  } else if (type === 'console') {
    body = <ConsoleWidget />;
  } else if (type === 'timer') {
    body = <TimerWidget dataKey={dataKey} />;
  } else if (isCountdownTimerWidget) {
    body = <CountdownTimerWidget id={id} countdownSeconds={options?.countdownSeconds} />;
  } else if (isBooleanWidget) {
    body = <BooleanWidget id={id} dataKey={dataKey} sensorKeys={sensorKeys} options={options} />;
  }

  const headerTitle = headerConfig.title ?? defaultTitle ?? '';
  const menuItems = headerConfig.menuItems;

  return (
    <WidgetHeaderProvider setHeaderConfig={setHeaderConfig}>
      <Box display="flex" flexDirection="column" overflow="hidden" width="100%" height="100%">
        <WidgetSettingsHeader
          title={headerTitle}
          onDelete={handleWidgetDelete}
          readOnly={isReadOnlyProject}
          allowDelete={canDeleteWidgets}
          menuItems={menuItems}
          toolbarEnd={headerConfig.toolbarEnd}
        />
        <Box key={`${id}-${type}-${dataKey}`} className="widget-body" flex="1" minHeight={0} overflow="hidden" display="flex" flexDirection="column">
          {body}
          {!hasKnownBody && <GraphNoDataMessage />}
        </Box>
      </Box>
    </WidgetHeaderProvider>
  );
};

export default WidgetBase;
